// Reusable section components for Carbon on Country
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export function SectionHeader({ number, label, title, italic, className = "" }) {
  return (
    <div className={`mb-12 md:mb-16 ${className}`}>
      <div className="flex items-baseline gap-6 mb-5">
        {number && <span className="section-number">— {number}</span>}
        {label && <span className="overline">{label}</span>}
      </div>
      <h2
        className="font-serif text-4xl md:text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-[#1F2A1B] max-w-4xl"
        data-testid="section-title"
      >
        {title}
        {italic && (
          <>
            <br />
            <span className="italic text-[#3F5132]">{italic}</span>
          </>
        )}
      </h2>
    </div>
  );
}

export function PullQuote({ children, attribution, testid }) {
  return (
    <blockquote
      data-testid={testid || "pull-quote"}
      className="border-l-2 border-[#C45A3C] pl-8 md:pl-12 my-16 md:my-24 max-w-3xl"
    >
      <p className="font-serif italic text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-[#3F5132]">
        {children}
      </p>
      {attribution && (
        <footer className="mt-6 overline text-[#8A8775]">{attribution}</footer>
      )}
    </blockquote>
  );
}

export function CapabilityList({ items, columns = 2, testid }) {
  return (
    <ul
      data-testid={testid || "capability-list"}
      className={`grid grid-cols-1 ${
        columns === 2 ? "md:grid-cols-2" : "md:grid-cols-3"
      } gap-x-12 gap-y-1 mt-8`}
    >
      {items.map((item, i) => (
        <li
          key={item}
          className="group flex items-start gap-4 py-5 border-b border-[#1F2A1B]/10 transition-colors hover:border-[#C45A3C]/60"
        >
          <span className="font-serif italic text-[#C45A3C] text-sm mt-1 w-10 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="text-[#1F2A1B] text-base md:text-lg leading-snug">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ImageBlock({ src, alt, caption, className = "", testid }) {
  return (
    <figure className={`relative img-hover ${className}`} data-testid={testid}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover warm-tint"
      />
      {caption && (
        <figcaption className="absolute bottom-4 left-4 right-4 text-[11px] tracking-[0.28em] uppercase text-[#1F2A1B] bg-[#F6F1E6]/70 backdrop-blur-sm px-3 py-2">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

export function CTAStrip({ heading, body, to = "/contact", label = "Get in touch", testid = "cta-strip" }) {
  return (
    <section
      data-testid={testid}
      className="border-y border-[#1F2A1B]/10 py-20 md:py-28 my-20"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <h3 className="font-serif text-3xl md:text-5xl leading-[1.05] text-[#1F2A1B]">
            {heading}
          </h3>
          {body && (
            <p className="text-[#4F5A48] text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              {body}
            </p>
          )}
        </div>
        <div className="lg:col-span-4 lg:text-right">
          <Link to={to} data-testid={`${testid}-cta`} className="btn-primary">
            {label}
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function Container({ children, className = "" }) {
  return (
    <div
      className={`max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16 ${className}`}
    >
      {children}
    </div>
  );
}
